/**
 * Settings › Accounts — the workspace's accounts (the credentials a destination needs), listed and
 * created without leaving Lookout
 * (rudder-specs lookout-audiences-initiative/activations/account-management §5).
 *
 * Its own top-level Settings tab since 2026-09-18, having been a third sub-tab under Connections. It
 * is the one settings surface that is an unbounded COLLECTION — GitHub and Warehouses are
 * small and near-fixed, while accounts grow with every destination a customer adds — so it runs on
 * the shared list stack (`useListControls` + `ListToolbar` + `filterSortItems`) with search, a Type
 * and an Auth filter, three sorts and a card view, as the sixth consumer after audiences,
 * activations, dashboards, workflows and the context hub.
 *
 * Reads go through the api-gateway list (no secret is ever in the payload); create is admin-only.
 * Row actions (Edit / Re-authorize / Delete) all live in the row's kebab; each opens a dialog or
 * drawer driven from state HERE, never from inside the menu's subtree. The re-authorize flow's
 * in-flight STATUS renders under the account's name in the table (see the `name` column's
 * `cellAside`) and below the grid in the card view — never inside a virtualized card.
 *
 * In a sibling workspace (ACT2-1124) the activation accounts of the readable workspaces (Settings ›
 * General) are rows of the same list, "From <workspace>", with Copy. Copy makes the account one of
 * this workspace's own accounts, and the activation wizard copies the same way.
 */
import type { RouterClient } from '@orpc/server';
import type { AppRouter } from '@server/rpc';
import { useQuery } from '@tanstack/react-query';
import { KeyRound, Plus } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ActionTooltip } from '@/_shared/components/ActionTooltip';
import { KebabMenu, type KebabMenuAction } from '@/_shared/components/list/KebabMenu';
import { ROW_ACTIONS_COLUMN } from '@/_shared/components/list/listColumns';
import { ListNoMatches } from '@/_shared/components/list/ListStates';
import { ListToolbar } from '@/_shared/components/list/ListToolbar';
import { VirtualizedCards } from '@/_shared/components/list/VirtualizedCards';
import { Table } from '@/_shared/components/Table/Table';
import type { TableColumn } from '@/_shared/components/Table/types';
import { Badge } from '@/_shared/components/ui/badge';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/_shared/components/ui/empty';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useListControls } from '@/_shared/hooks/useListControls';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';
import { useRudderstackConfigured } from '@/_shared/hooks/useRudderstackConfigured';
import {
  useIsSiblingWorkspace,
  useMissingRudderstackGrant,
  useRudderstackGrantsDecide,
} from '@/_shared/hooks/useSiblingStatus';
import { filterSortItems, type SortState } from '@/_shared/lib/list-controls';
import { destinationTypeIcon } from '@/audiences/icons/destinationTypes';

import { AccountCopyContext, type AccountCopyContextValue } from '../components/accountCopies/accountCopyContext';
import { CopyAccountButton } from '../components/accountCopies/CopyAccountButton';
import { useAccountCopyPick } from '../components/accountCopies/useAccountCopyPick';
import { useOrganizationDestinationAccounts } from '../components/accountCopies/useOrganizationDestinationAccounts';
import { AccountCard } from '../components/rudderstackAccount/AccountCard';
import { AddRudderstackAccountDrawer } from '../components/rudderstackAccount/AddRudderstackAccountDrawer';
import { DeleteAccountDialog } from '../components/rudderstackAccount/DeleteAccountDialog';
import { EditAccountDrawer } from '../components/rudderstackAccount/EditAccountDrawer';
import { OrganizationAccountCard } from '../components/rudderstackAccount/OrganizationAccountCard';
import { buildAccountRowActions } from '../components/rudderstackAccount/permissions';
import { ReauthorizeAction } from '../components/rudderstackAccount/ReauthorizeAction';
import { RudderstackNotConfiguredCard } from '../components/RudderstackNotConfiguredCard';
import { SettingsCardsSkeleton } from '../components/SettingsCardsSkeleton';
import { SettingsSectionHeader } from '../components/SettingsSectionHeader';
import {
  ACCOUNT_DEFAULT_SORT,
  ACCOUNT_DERIVE_CONFIG,
  ACCOUNT_FILTER_KEYS,
  ACCOUNT_SORT_KEYS,
  ACCOUNT_SORT_OPTIONS,
  accountAuthLabel,
  accountConnectedAs,
  accountFilterGroups,
  type AccountFilterKey,
  type AccountListRow,
  type AccountsListEntry,
  type AccountSortKey,
  isOrganizationAccountRow,
} from '../lib/accountList';

/** One definition as `listDefinitions` returns it — derived from the router, never re-declared. */
type Definition = Awaited<
  ReturnType<RouterClient<AppRouter>['settings']['rudderstackAccounts']['listDefinitions']>
>['definitions'][number];

export function SettingsAccounts(): React.ReactElement {
  usePageTitle('Accounts — Settings');
  const { isAdmin, wp } = useWorkspace();
  // Null while the sibling status loads: the readable workspaces' accounts may still come, so no empty state yet.
  const siblingState = useIsSiblingWorkspace();
  const isSiblingWorkspace = siblingState === true;
  // ACT2-1124 LLD §6 (F13a): in a sibling workspace RudderStack's grants decide account writes — the server answers
  // 403 with the grant set — so the Lookout admin floor applies only outside one. There, a person who lacks
  // "Create, delete and edit activations" (it covers ad accounts) sees the controls off, with that set as the reason.
  const grantsDecide = useRudderstackGrantsDecide();
  const missingAccountGrant = useMissingRudderstackGrant('activations');
  const canWriteAccounts = grantsDecide ? missingAccountGrant === null : isAdmin;
  const addAccountDeniedReason = missingAccountGrant ?? 'Only workspace admins can add accounts';
  const orpc = useOrpc();
  const configured = useRudderstackConfigured();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingAccount, setDeletingAccount] = useState<{ id: string; name: string } | null>(null);
  /**
   * Which account the re-authorize flow belongs to, and whether its confirm dialog is open — TWO
   * pieces of state, and the split is load-bearing.
   *
   * Both live HERE, at the page, and not inside `ReauthorizeAction` or the kebab: the menu item that
   * opens the dialog is unmounted by Radix the instant the menu closes on select, so a dialog driven
   * from inside that subtree would be destroyed by the very click that asked for it.
   *
   * They were one nullable id until the card view arrived, and one was enough only because the TABLE
   * mounts a `ReauthorizeAction` for every eligible row unconditionally — the flow's host never
   * depended on the dialog. The card view mounts exactly ONE, for the account being re-authorized
   * (a virtualized card cannot host it — see the grid below), and `confirmAndStart` opens the popup
   * and THEN closes the dialog. A single flag would therefore unmount the live flow one tick after it
   * began, taking the popup handle, the nonce, the listener and the abandon timer with it. The
   * account id outlives the dialog instead — the same split, for the same reason, as `AccountField`.
   */
  const [reauthAccountId, setReauthAccountId] = useState<string | null>(null);
  const [confirmReauthOpen, setConfirmReauthOpen] = useState(false);
  const openReauthorize = useCallback((id: string): void => {
    setReauthAccountId(id);
    setConfirmReauthOpen(true);
  }, []);

  // `scope: 'manage'` on BOTH, and stated rather than left to the default: this table is where a
  // workspace maintains the accounts it already has, which includes accounts on a grant Lookout no
  // longer offers for new activations. Under the default `'select'` scope those rows are absent
  // entirely — no edit, no delete, no re-authorize — while their destinations keep delivering.
  // `AddRudderstackAccountDrawer` below deliberately does NOT get this: creating is a select-scope
  // question, and the two are the same no-argument call otherwise.
  const accountsQuery = useQuery({
    ...orpc.settings.rudderstackAccounts.list.queryOptions({ input: { scope: 'manage' } }),
    enabled: configured.isConfigured,
  });
  const definitionsQuery = useQuery({
    ...orpc.settings.rudderstackAccounts.listDefinitions.queryOptions({ input: { scope: 'manage' } }),
    enabled: configured.isConfigured,
  });

  const definitionsByName = useMemo(
    () => new Map<string, Definition>((definitionsQuery.data?.definitions ?? []).map((d) => [d.name, d])),
    [definitionsQuery.data],
  );
  const definitionsPending = definitionsQuery.isPending;

  /**
   * The list. Nothing transient belongs in here.
   *
   * The re-authorize dialog's open flag used to ride each row, which was free while `rows` went
   * straight to `Table` and stopped being free once `filterSortItems` and `accountFilterGroups` ran
   * off the same array. It was also a correctness bug: `Table` resets its own `scrollTop` when `data`
   * changes identity, so toggling the dialog jumped the table to the top — and past
   * `AUTO_VIRTUALIZE_THRESHOLD` (100 rows) that unmounted the very row the dialog belonged to, along
   * with the live OAuth flow beside it.
   *
   * Moving the flag one memo downstream only moved the problem, because that memo WAS `Table`'s
   * `data`. The flag is gone instead: one `ReauthorizeAction` for the page (see the render below)
   * needs no per-row state at all.
   */
  const rows: AccountListRow[] = useMemo(
    () =>
      (accountsQuery.data?.accounts ?? []).map((a) => ({
        ...a,
        typeLabel: definitionsByName.get(a.accountDefinitionName)?.destinationDisplayName ?? a.accountDefinitionName,
        authLabel: accountAuthLabel(a.authenticationType),
      })),
    [accountsQuery.data, definitionsByName],
  );

  // ACT2-1124: the activation accounts of the readable workspaces, and their Copy. A copy is one of this workspace's
  // own accounts once `settings.rudderstackAccounts.list` reads it again, so its original's row goes.
  const ownAccountIds = useMemo(() => new Set(rows.map((row) => row.id)), [rows]);
  const organization = useOrganizationDestinationAccounts({ enabled: isSiblingWorkspace, ownAccountIds });
  const copyPick = useAccountCopyPick();
  const copyContext: AccountCopyContextValue = { pick: copyPick, blockedReason: missingAccountGrant };
  const entries: AccountsListEntry[] = useMemo(() => [...rows, ...organization.rows], [rows, organization.rows]);
  const hasOrganizationRows = organization.rows.length > 0;
  const organizationLoading = siblingState === null || organization.isLoading;

  // Gating (shared UI notes §4 / ui-tasks-shared-notes.md §11): Re-authorize is OAuth-only. Edit is
  // gated on the definition's `lookoutSupport === 'form'`, NOT "not OAuth" — a non-OAuth definition
  // with an empty form (Postgres/Snowflake/BigQuery sources) resolves to `'external'` and has nothing
  // to render. Delete appears for every account.
  const actionsFor = useCallback(
    (row: AccountListRow): KebabMenuAction[] =>
      buildAccountRowActions(row, definitionsByName.get(row.accountDefinitionName), {
        isAdmin: canWriteAccounts,
        ...(missingAccountGrant === null ? {} : { deniedReason: missingAccountGrant }),
        onEdit: (r) => setEditingId(r.id),
        onDelete: (r) => setDeletingAccount({ id: r.id, name: r.name }),
        // Opens the confirm dialog ONLY. The OAuth popup is opened by the dialog's own button, inside
        // that click's gesture — never from here, where the menu's teardown would race it.
        onReauthorize: (r) => openReauthorize(r.id),
        // Rows paint on the accounts query alone, so Edit renders disabled-with-a-reason rather than
        // absent until this settles — no affordance appearing under an already-open kebab.
        definitionsPending,
      }),
    [definitionsByName, canWriteAccounts, missingAccountGrant, definitionsPending, openReauthorize],
  );

  /**
   * Memoized DELIBERATELY, and the dependency list is part of the contract. `Table` rebuilds its
   * TanStack column defs whenever this array's identity changes, and `flexRender` renders each `cell`
   * as a React element TYPE — so a fresh array on every render unmounts and remounts every cell.
   * `ReauthorizeAction` lives in the NAME cell (`cellAside`, since 2026-09-10 — the 3.5rem actions
   * column clipped it) and holds a live OAuth flow (popup handle, nonce, BroadcastChannel listener,
   * abandon timer); remounting it mid-consent drops all of that with no error, which is why the
   * open-dialog flag travels on the ROW DATUM (`confirmingReauth`) instead of being closed over here.
   * Nothing per-row-state may enter these deps.
   */
  const columns = useMemo<TableColumn<AccountsListEntry>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: (r) => {
          if (isOrganizationAccountRow(r)) {
            return (
              <span className="flex min-w-0 flex-col">
                <span className="truncate">{r.name}</span>
                <span className="truncate text-xs text-muted-foreground">From {r.option.workspaceName}</span>
              </span>
            );
          }
          // `accountConnectedAs` owns the OAuth-only rule, for all four of its callers — this table,
          // `AccountCard`, `AccountRow` and `SyncAccountRow` — rather than each restating the ternary.
          const connectedAs = accountConnectedAs(r);
          return (
            <span className="flex min-w-0 flex-col">
              <span>{r.name}</span>
              {connectedAs && <span className="text-xs text-muted-foreground">{connectedAs}</span>}
              {/* Vamsi L4: without the platform's `externalId`,
                  two accounts a customer named the same are identical rows — and this is the table
                  whose kebab edits and deletes them. Null for most accounts, and a null renders
                  nothing rather than an empty line. */}
              {r.externalId && <span className="block truncate text-xs text-muted-foreground">{r.externalId}</span>}
            </span>
          );
        },
      },
      {
        id: 'typeLabel',
        header: 'Type',
        cell: (r) => {
          const Icon = destinationTypeIcon(r.destinationType).Icon;
          return (
            <span className="flex items-center gap-2">
              {/* Decorative — the cell's own text is already the accessible name. */}
              <span aria-hidden className="flex shrink-0 items-center">
                <Icon className="size-4" />
              </span>
              <span>{r.typeLabel}</span>
            </span>
          );
        },
      },
      {
        id: 'authLabel',
        header: 'Auth',
        cell: (r) => (
          <Badge variant="outline" className="text-xs">
            {r.authLabel}
          </Badge>
        ),
      },
      {
        ...ROW_ACTIONS_COLUMN,
        // The kebab and nothing else — this column is 3.5rem wide, which is exactly what it holds. Rows of
        // readable workspaces (ACT2-1124) hold Copy instead, so the column widens while one is listed.
        ...(hasOrganizationRows ? { width: '6.5rem' } : {}),
        cell: (r) => {
          if (isOrganizationAccountRow(r)) return <CopyAccountButton row={r} />;
          const actions = actionsFor(r);
          if (actions.length === 0) return null;
          return <KebabMenu label={`Actions for ${r.name}`} actions={actions} />;
        },
      },
    ],
    // `actionsFor` alone now. The other three were read by the per-row `cellAside` that hosted the
    // re-authorize surface; with one instance for the page instead, nothing in these columns touches
    // them. Keeping the array minimal is the point — a change of identity here rebuilds the column
    // defs and remounts every cell.
    [actionsFor, hasOrganizationRows],
  );

  const controls = useListControls<AccountSortKey, AccountFilterKey>({
    sortKeys: ACCOUNT_SORT_KEYS,
    defaultSort: ACCOUNT_DEFAULT_SORT,
    filterKeys: ACCOUNT_FILTER_KEYS,
    // `filterOptions` is deliberately NOT passed. It sanitises hand-edited URL values against a known
    // list, but this list's Type values are derived from the accounts THEMSELVES, which have not
    // arrived on the first render — so an allow-list built there would be empty exactly when a deep
    // link's `f_type` is read, and would silently drop a perfectly good filter. Letting an unknown
    // value through instead filters to nothing, which is visible and which "Clear all" undoes.
    defaultView: 'list',
    persistKey: 'settings-accounts',
  });

  const visible = useMemo(
    () =>
      filterSortItems(
        entries,
        { query: controls.query, sort: controls.sort, filters: controls.filters },
        ACCOUNT_DERIVE_CONFIG,
      ),
    [entries, controls.query, controls.sort, controls.filters],
  );
  // Built from every row, not from `visible`: options that vanish as you narrow would make the menu
  // rearrange under the cursor, and the one you just picked would be the first to go.
  const filterGroups = useMemo(() => accountFilterGroups(entries), [entries]);

  /** The account a card-view `ReauthorizeAction` belongs to — see the grid below for why there is one. */
  const reauthCardAccount = reauthAccountId === null ? undefined : rows.find((r) => r.id === reauthAccountId);

  const renderCard = useCallback(
    (account: AccountsListEntry) =>
      isOrganizationAccountRow(account) ? (
        <OrganizationAccountCard account={account} />
      ) : (
        <AccountCard account={account} actions={actionsFor(account)} />
      ),
    [actionsFor],
  );

  const addButton = (
    <ActionTooltip show={!canWriteAccounts} message={addAccountDeniedReason}>
      <Button
        type="button"
        variant="primary"
        Icon={<Plus className="size-4" />}
        disabled={!canWriteAccounts}
        onClick={() => setDrawerOpen(true)}
        label="Add account"
      />
    </ActionTooltip>
  );

  const header = (
    <SettingsSectionHeader
      title="Accounts"
      description="The accounts a destination uses to deliver — the credential behind an activation."
      action={addButton}
    />
  );

  if (configured.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <SettingsCardsSkeleton />
      </div>
    );
  }
  if (configured.isNotConfigured) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <RudderstackNotConfiguredCard description="Sign in with RudderStack to link a workspace, and the accounts its destinations deliver with will appear here." />
      </div>
    );
  }

  // The settings shell gives this tab a fixed height (`SettingsShell`), so the list takes the rest of it.
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {header}

      {accountsQuery.isError && <Callout variant="error">Could not load accounts.</Callout>}

      {organization.unavailable !== null && (
        <Callout variant="error">
          Could not read the accounts of your organization’s other workspaces just now, so only this workspace’s
          accounts are listed. Refresh to try again.
        </Callout>
      )}

      {/* ACT2-1124 (D18): accounts of other workspaces show only for the workspaces in the General tab's list. */}
      {isSiblingWorkspace && organization.readableWorkspaceCount === 0 && (
        <Callout variant="info">
          To copy activation accounts of other RudderStack workspaces, add the workspaces in{' '}
          <Link to={wp('/settings')} className="font-medium underline">
            Settings › General
          </Link>
          .
        </Callout>
      )}

      {/* The platform needs a new sign-in to copy this account (F22c): the person adds it here instead. */}
      {copyPick.signInMessage !== null && (
        <Callout variant="warning">
          <p>{copyPick.signInMessage}</p>
          <div className="mt-2">
            <ActionTooltip show={!canWriteAccounts} message={addAccountDeniedReason}>
              <Button
                type="button"
                variant="secondary"
                size="md"
                label="Add a new account"
                disabled={!canWriteAccounts}
                onClick={() => setDrawerOpen(true)}
              />
            </ActionTooltip>
          </div>
        </Callout>
      )}
      {copyPick.checkRefusal !== null && <Callout variant="error">{copyPick.checkRefusal}</Callout>}

      {(accountsQuery.isLoading || (accountsQuery.isSuccess && rows.length === 0 && organizationLoading)) && (
        <SettingsCardsSkeleton />
      )}

      {accountsQuery.isSuccess && entries.length === 0 && !organizationLoading && (
        <Empty className="border">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <KeyRound />
            </EmptyMedia>
            <EmptyTitle>No accounts yet</EmptyTitle>
            <EmptyDescription>
              Add the account a destination needs, and activations can pick it without leaving Lookout.
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <ActionTooltip show={!canWriteAccounts} message={addAccountDeniedReason}>
              {/* A distinct name, not a second "Add account" (Leo #6): with the empty state on
                  screen there are two buttons doing this, and a screen-reader or voice-control user
                  choosing between two identically-named controls has nothing to go on. The visible
                  label carries the distinction rather than an aria-label that disagrees with it. */}
              <Button
                type="button"
                variant="secondary"
                Icon={<Plus className="size-4" />}
                disabled={!canWriteAccounts}
                onClick={() => setDrawerOpen(true)}
                label="Add your first account"
              />
            </ActionTooltip>
          </EmptyContent>
        </Empty>
      )}

      {accountsQuery.isSuccess && entries.length > 0 && (
        <AccountCopyContext.Provider value={copyContext}>
          <ListToolbar
            query={controls.query}
            onQueryChange={controls.setQuery}
            searchPlaceholder="Search accounts…"
            view={controls.view}
            onViewChange={controls.setView}
            sortOptions={ACCOUNT_SORT_OPTIONS}
            sort={controls.sort}
            onSortChange={(next) => controls.setSort(next as SortState<AccountSortKey>)}
            defaultSort={ACCOUNT_DEFAULT_SORT}
            filterGroups={filterGroups}
            filters={controls.filters}
            onFilterChange={(key, value) => controls.setFilter(key as AccountFilterKey, value)}
            onClearFilters={controls.clearFilters}
            onClearAll={controls.clearAll}
            totalCount={entries.length}
            resultCount={visible.length}
            itemNoun={{ singular: 'account', plural: 'accounts' }}
          />

          {visible.length === 0 ? (
            <ListNoMatches
              noun="accounts"
              onClear={() => {
                controls.setQuery('');
                controls.clearFilters();
              }}
            />
          ) : controls.view === 'list' ? (
            // The table takes the rest of the tab's height: its header stays in view and its rows scroll.
            <div className="min-h-0 flex-1">
              <Table<AccountsListEntry> data={visible} getRowId={(r) => r.id} columns={columns} fillHeight />
            </div>
          ) : (
            // The grid scrolls itself in the rest of the tab's height (`h-full`). It is built for a full-bleed
            // page: it centres its content at `maxContentRem` and then insets it by its own `px-6`. Here the
            // shell has already applied the page gutter, so left alone the cards sit 24px right of the toolbar
            // directly above them. `-mx-6` gives the grid those 24px back on each side and `maxContentRem` is
            // widened by the same 3rem, so its inner padding lands the cards exactly on the toolbar. Two columns
            // match the shell's own `max-w-3xl` content width.
            <div className="min-h-0 flex-1">
              <VirtualizedCards
                className="-mx-6 h-full"
                items={visible}
                getItemKey={(a) => a.id}
                renderItem={renderCard}
                minColumnRem={20}
                maxColumns={2}
                maxContentRem={51}
                estimateRowHeight={160}
              />
            </div>
          )}

          {/* ONE `ReauthorizeAction` for the whole page — both views, outside every branch above.
              It holds a live popup handle, nonce, BroadcastChannel listener and abandon timer, and
              its unmount runs `teardown(false)`: no `oauthAbandon`, popup left open, nothing left to
              receive the provider's callback. So every way the list can re-render it away is a
              silent orphan, and there were four — a virtualized card scrolled out of view, the
              `ListNoMatches` swap when a search matches nothing, the list/card toggle, and (in the
              table) `Table` resetting its scroll when `data` changes identity, which past 100 rows
              unmounts the row.
              The table used to mount one per row, which covered none of those and is why
              `confirmingReauth` had to ride the row datum at all. One instance resolved from the
              UNFILTERED `rows` is immune to all four, and lets `visible` go straight to `Table`.
              Known limitation, unchanged: re-targeting mid-flow (a second account's kebab while the
              first is still waiting) swaps this instance's `account` without resetting its hook — see
              `reauthAccountId` above. */}
          {reauthCardAccount ? (
            <ReauthorizeAction
              account={{
                id: reauthCardAccount.id,
                name: reauthCardAccount.name,
                accountDefinitionName: reauthCardAccount.accountDefinitionName,
              }}
              surface="settings"
              confirmOpen={confirmReauthOpen}
              onConfirmOpenChange={(open) =>
                open ? openReauthorize(reauthCardAccount.id) : setConfirmReauthOpen(false)
              }
            />
          ) : null}
        </AccountCopyContext.Provider>
      )}

      <AddRudderstackAccountDrawer open={drawerOpen} onOpenChange={setDrawerOpen} surface="settings" />

      {editingId && (
        <EditAccountDrawer
          accountId={editingId}
          open
          onOpenChange={(next) => {
            if (!next) setEditingId(null);
          }}
          surface="settings"
          onSaved={() => setEditingId(null)}
        />
      )}

      {deletingAccount && (
        <DeleteAccountDialog
          account={deletingAccount}
          open
          onOpenChange={(next) => {
            if (!next) setDeletingAccount(null);
          }}
          surface="settings"
          onDeleted={() => setDeletingAccount(null)}
        />
      )}
    </div>
  );
}
